import React, { useState } from 'react';
import axios from 'axios';
import DividendForm from './DividendForm';
import './RealizedGains.css';
import { API_BASE_URL } from '../lib/api';

function DividendsTable({ dividends, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [editingDividend, setEditingDividend] = useState(null);

  const handleEdit = (dividend) => {
    setEditingDividend(dividend);
    setShowForm(true);
  };

  const handleDelete = async (transactionId) => {
    if (!window.confirm('Are you sure you want to delete this dividend?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/transactions/dividends/${transactionId}`, {
        withCredentials: true
      });
      onRefresh();
    } catch (err) {
      console.error('Error deleting dividend:', err);
      alert('Failed to delete dividend');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingDividend(null);
    onRefresh();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingDividend(null);
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="dividends-section">
      <div className="section-header">
        <button onClick={() => setShowForm(true)} className="btn-add">
          + Add Dividend
        </button>
      </div>

      {showForm && (
        <DividendForm
          dividend={editingDividend}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}

      {dividends.length === 0 ? (
        <div className="empty-state">
          <p>No dividends recorded for this year.</p>
          <p className="empty-hint">Click "+ Add Dividend" to record a dividend payment.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="dividends-table">
            <thead>
              <tr>
                <th>Payment Date</th>
                <th>Asset</th>
                <th>Dividend/Share</th>
                <th>Shares</th>
                <th>Total Amount (EUR)</th>
                <th>Tax Withheld</th>
                <th>Net After Tax</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {dividends.map(div => {
                const netAfterTax = div.totalDividendAmountEUR - (div.taxWithheld || 0);
                const isAnnual = div.statementType === 'ANNUAL';
                return (
                  <tr key={div.transactionId}>
                    <td>{formatDate(div.date)}</td>
                    <td>
                      <div className="asset-cell">
                        {isAnnual ? (
                          <>
                            <strong>Annual Statement</strong>
                            <br />
                            <span className="ticker">{div.broker}</span>
                          </>
                        ) : (
                          <>
                            <strong>{div.assetName}</strong>
                            <br />
                            <span className="ticker">{div.ticker}</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td>{isAnnual ? '-' : formatCurrency(div.dividendPerShare, div.currency)}</td>
                    <td>{isAnnual ? '-' : div.sharesOwned}</td>
                    <td className="dividend-amount">
                      {formatCurrency(div.totalDividendAmountEUR, 'EUR')}
                      {div.currency !== 'EUR' && (
                        <div className="original-currency">
                          ({formatCurrency(div.totalDividendAmount, div.currency)})
                        </div>
                      )}
                    </td>
                    <td>{formatCurrency(div.taxWithheld || 0, div.currency)}</td>
                    <td className="net-amount">
                      {formatCurrency(netAfterTax, 'EUR')}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEdit(div)}
                          className="btn-edit"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(div.transactionId)}
                          className="btn-delete"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {dividends.length > 0 && (
        <div className="table-summary">
          <p>Total: {dividends.length} dividend payment{dividends.length !== 1 ? 's' : ''}</p>
        </div>
      )}
    </div>
  );
}

export default DividendsTable;
